import React, { useEffect, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import GearSpinner from '../../../components/GearSpinner/GearSpinner';
import Loader from '../../../components/Loader/Loader';
import SalesTrendingReportService from '../../../services/SalesTrendingReportService';
import shareIcon from '../../../assets/images/share-img.png';
import pdfIcon from '../../../assets/images/pdf-img.png';
import xlsIcon from '../../../assets/images/xls-img.png';
import ShareSalesReportModal from '../../../components/Modals/ShareSalesReportModal';
import Select from 'react-select';
import moment from 'moment';

const CustomerRankingTab = ({ active, showDataFor }) => {
    const { user } = useAuth();

    const [salesData, setSalesData] = useState([]);
    const [dormantCustomers, setDormantCustomers] = useState([]);
    const [newCustomers, setNewCustomers] = useState([]);
    const [dormantCustomersLoader, setDormantCustomersLoader] = useState(null);
    const [newCustomersLoader, setNewCustomersLoader] = useState(null);
    const [topCustomerSales, setTopCustomerSales] = useState([]);
    const [loading, setLoading] = useState(null); 
    const [customerFilterData, setCustomerFilterData] = useState([]);
    const [stateFilterData, setStateFilterData] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [showLoader, setShowLoader] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [sharePayload, setSharePayload] = useState(null);
    const salesTrendingReportService = new SalesTrendingReportService();

    const [isExportReady, setIsExportReady] = useState(false);
    
    const currentYear = moment().year();
    const previousYear = moment().year() - 1;
    const nextPreviousYear = moment().year() - 2;
    
    const months = [];

    for (let i = 7; i >= 0; i--) {
        months.push(
            moment().subtract(i, 'months').format('MMM-YY')
        );
    }

    
    useEffect(() => {
        if (!active) return;

        fetchTopCustomerSales();
        fetchNewCustomerSales();
        fetchDormantCustomers();

    }, [active, showDataFor]);

    useEffect(() => {
        if (topCustomerSales.length > 0) {
            setSalesData(topCustomerSales);
        }
    }, [topCustomerSales]);

    useEffect(() => {
        let filteredData = [...topCustomerSales];

        if (selectedCustomer) {
            filteredData = filteredData.filter(
                c => c.customer_name === selectedCustomer.value
            );
        }

        if (selectedState) {
            filteredData = filteredData.filter(
                c => `${c.city}, ${c.state_code}` === selectedState.value
            );
        }

        setSalesData(filteredData);
    }, [selectedCustomer, selectedState, topCustomerSales]);

    useEffect(() => {
        if(topCustomerSales.length > 0 && dormantCustomers.length > 0 &&  newCustomers.length > 0) {
            setIsExportReady(true);
        } else {
            setIsExportReady(false);
        }

    },[topCustomerSales, dormantCustomers, newCustomers]);

    const handleCustomerChange = (option) => {
        setSelectedCustomer(option);
    };

    const handleStateChange = (option) => {
        setSelectedState(option);
    };


    if (!active) return null;

    const buildPayload = (charts) => ({
        tab: 'customer-ranking',
        meta: {
            portal_user_name: showDataFor,
            generated_at: new Date().toISOString(),
            start_date: moment().subtract(4, 'months').format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD')
        },
        top_customers: salesData,
        dormant_customers: dormantCustomers,
        new_customers: newCustomers

    });

    const handleDownloadPDF = async () => {
        // 3. Prevent click if data isn't ready
        if (!isExportReady) return;

        setShowLoader(true);
        try {
            salesTrendingReportService.exportSalesTrendingPdf(buildPayload()).then((response) => {
                const blob = response.data;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const formattedDate = moment().format('MM-DD-YYYY_HH-mm');
                const title = `${showDataFor}-sales-trending-report-${formattedDate}`;
                link.href = url;
                link.download = `${title}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                setShowLoader(false);
            }).catch((error) => {
                console.error('Error downloading file:', error);
                setShowLoader(false);
            });
        } catch (error) {
            console.error('Error downloading datasheet:', error);
            setShowLoader(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!isExportReady) return; // Guard clause
        setShowLoader(true);

        try {
            salesTrendingReportService.exportSalesTrendingExcel(buildPayload()).then((response) => {
                const blob = response.data;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const formattedDate = moment().format('MM/DD/YYYY HH:mm');
                const title = `${showDataFor}-sales-trending-report-${formattedDate}`;
                link.href = url;
                link.download = `${title}.xlsx`;
                link.click();
                window.URL.revokeObjectURL(url);
                setShowLoader(false);

            });
        } catch (error) {
            console.error('Error downloading datasheet:', error);
            setShowLoader(false);
        }
    }

    const handleShareClick = () => {
        const payload = buildPayload();
        setSharePayload(payload);
        setShowShareModal(true);
    };
    

    const fetchTopCustomerSales = () => {
        setLoading(true);
        var payload = {
            "portal_user_name": showDataFor
        };

        salesTrendingReportService.getTopCustomers(payload).then((response) => {
            setLoading(false);
            if (response.status === 200) {
                if (response.data.status === 'success') {
                    const customerFilterData = [
                        ...new Map(
                            response.data.result.map(c => [
                                c.customer_name,
                                { value: c.customer_name, label: c.customer_name }
                            ])
                        ).values()
                    ].sort((a, b) => a.label.localeCompare(b.label));
                    
                    const stateFilterData = [
                        ...new Map(
                            response.data.result.map(c => [
                                `${c.city}, ${c.state_code}`,
                                {
                                    value: `${c.city}, ${c.state_code}`,
                                    label: `${c.city}, ${c.state_code}`
                                }
                            ])
                        ).values()
                    ].sort((a, b) => a.label.localeCompare(b.label));


                    setTopCustomerSales(response.data.result);
                    setCustomerFilterData(customerFilterData);
                    setStateFilterData(stateFilterData);


                } else {
                    console.error('Error:', response.data.message);
                }
            } else {
                console.error('HTTP Error:', response.status);
            }
        });
    }
    
    const fetchNewCustomerSales = () => {
        setNewCustomersLoader(true);
        // Implement the logic to fetch new customer sales data
        var payload = {
            "portal_user_name": showDataFor,
            "start_date": moment().subtract(4, 'months').format('YYYY-MM-DD'),
            "end_date": moment().format('YYYY-MM-DD')
        };
        
        salesTrendingReportService.getNewCustomerSales(payload).then((response) => {
            setNewCustomersLoader(false);
            if (response.status === 200) {
                if (response.data.status === 'success') {
                    setNewCustomers(response.data.result);
                } else {
                    console.error('Error:', response.data.message);
                }
            } else {
                console.error('HTTP Error:', response.status);
            }
        });
    };
    
    const fetchDormantCustomers = () => {
        setDormantCustomersLoader(true);
        // Implement the logic to fetch dormant customers data
        var payload = {
            "portal_user_name": showDataFor,
            "start_date": moment().subtract(4, 'months').format('YYYY-MM-DD'),
            "end_date": moment().format('YYYY-MM-DD')
        };
        
        salesTrendingReportService.getDormantCustomers(payload).then((response) => {
            setDormantCustomersLoader(false);
            if (response.status === 200) {
                if (response.data.status === 'success') {
                    setDormantCustomers(response.data.result);
                } else {
                    console.error('Error:', response.data.message);
                }
            } else {
                console.error('HTTP Error:', response.status);
            }
        });
    };

    const renderTopCustomers = () => {
        return(
            <React.Fragment>
                <div className="row">
                    <div className="col-md-12">
                        <div className="customer-ranking-table-container">
                            <table className="table table-bordered customer-ranking-table">
                                <thead>
                                    <tr>
                                        <th colSpan="18" className="report-title">
                                            Customer Sales Data - {showDataFor}
                                        </th>
                                    </tr>

                                    <tr>
                                        <th colSpan="18" className="report-subtitle">
                                            Top 95 Customers - 12 Months Running
                                        </th>
                                    </tr>

                                    <tr>
                                        <th colSpan="3"></th>
                                        <th colSpan="15" className="group-header">Sales in USD</th>
                                    </tr>

                                    <tr>
                                        <th>Rank</th>
                                        <th>Customer</th>
                                        <th>City, State</th>
                                        <th>{nextPreviousYear}</th>
                                        <th>{previousYear}</th>
                                        <th>{currentYear} YTD</th>
                                        <th>Previous 12 Months</th>
                                        <th>Last 12 Months</th>
                                        <th>Change</th>
                                        {months.map((month, index) => (
                                            <th key={index}>{month}</th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading && (
                                        <tr>
                                            <td colSpan="18" className="text-center py-4">
                                                <GearSpinner />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && salesData.length === 0 && (
                                        <tr>
                                            <td colSpan="18" className="text-center py-4">
                                                No data available
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && salesData.length > 0 && salesData.map((customer, index) => (
                                        <tr key={index}>
                                            <td>{customer.rank}</td>
                                            <td>{customer.customer_name}</td>
                                            <td>{customer.city}, {customer.state_code}</td>
                                            <td>{salesData[index][`sales_${nextPreviousYear}`]}</td>
                                            <td>{salesData[index][`sales_${previousYear}`]}</td>
                                            <td>{salesData[index][`sales_${currentYear}`]}</td>
                                            <td>{customer.sales_prev_12_months}</td>
                                            <td>{customer.sales_last_12_months}</td>
                                            <td className={customer.change === 0 ? '' : customer.change < 0 ? 'text-danger' : 'text-success'}>{`${customer.change}%`}</td>
                                            {Object.keys(customer.monthly_sales).map((monthSale, mIndex) => (
                                                <td key={mIndex}>{customer.monthly_sales[monthSale]}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        )
    }

    const renderNewCustomers = () => {
        return(
            <React.Fragment>
                <div className="col-md-6 text-center fw-bold">
                    <table className="table table-bordered customer-ranking-legend-table">
                        <thead>
                            <tr>
                                <th colSpan="4" className="legend-title">New Customers (Sales in past 4 months)</th>
                            </tr>
                            <tr>
                                <td>Rank</td>
                                <td>Customer</td>
                                <td>Rank</td>
                                <td>Customer</td>
                            </tr>
                        </thead>
                        <tbody>
                            {newCustomersLoader ? (
                                <tr>
                                <td colSpan="4" className="text-center py-4">
                                    <GearSpinner />
                                </td>
                                </tr>
                            ) : newCustomers.length === 0 ? (
                                <tr>
                                <td colSpan="4" className="text-center py-4">
                                    No data available
                                </td>
                                </tr>
                            ) : (() => {
                                const half = Math.ceil(newCustomers.length / 2);
                                const leftColumn = newCustomers.slice(0, half);
                                const rightColumn = newCustomers.slice(half);

                                return leftColumn.map((customer, index) => (
                                    <tr key={customer.customer_id || index}>
                                    <td>{customer.rank}</td>
                                    <td>{customer.customer_id ? `${customer.customer_name} , ${customer.city} , ${customer.state_code}` : 'Slot Unfilled'}</td>
                                    <td>{rightColumn[index]?.rank || ''}</td>
                                    <td>
                                        {rightColumn[index]?.customer_id
                                        ? `${rightColumn[index].customer_name} , ${rightColumn[index].city} , ${rightColumn[index].state_code}`
                                        : 'Slot Unfilled'}
                                    </td>
                                    </tr>
                                ));
                            })()}
                        </tbody>

                    </table>
                </div>
            </React.Fragment>
        )
    }

    const renderDormantCustomers = () => {
        return(
            <React.Fragment>
                <div className="col-md-6 text-center fw-bold">
                    <table className="table table-bordered customer-ranking-legend-table">
                        <thead>
                            <tr>
                                <th colSpan="4" className="legend-title">Dormant Customers (No Sales in past 3 months)</th>
                            </tr>
                            <tr>
                                <td>Rank</td>
                                <td>Customer</td>
                                <td>Rank</td>
                                <td>Customer</td>
                            </tr>
                        </thead>
                        <tbody>
                            {dormantCustomersLoader ? (
                                <tr>
                                <td colSpan="4" className="text-center py-4">
                                    <GearSpinner />
                                </td>
                                </tr>
                            ) : dormantCustomers.length === 0 ? (
                                <tr>
                                <td colSpan="4" className="text-center py-4">
                                    No data available
                                </td>
                                </tr>
                            ) : (() => {
                                const half = Math.ceil(dormantCustomers.length / 2);
                                const leftColumn = dormantCustomers.slice(0, half);
                                const rightColumn = dormantCustomers.slice(half);

                                return leftColumn.map((customer, index) => (
                                    <tr key={customer.customer_id || index}>
                                    <td>{customer.rank}</td>
                                    <td>{customer.customer_id ? `${customer.customer_name} , ${customer.city} , ${customer.state_code}` : 'Slot Unfilled'}</td>
                                    <td>{rightColumn[index]?.rank || ''}</td>
                                    <td>
                                        {rightColumn[index]?.customer_id
                                        ? `${rightColumn[index].customer_name} , ${rightColumn[index].city} , ${rightColumn[index].state_code}`
                                        : 'Slot Unfilled'}
                                    </td>
                                    </tr>
                                ));
                            })()}
                        </tbody>
                    </table>
                </div>
            </React.Fragment>
        )
    }


    return (
        <div className="container tab-pane active">
            <div className="row mb-4">
                <div className="col-md-4">
                    <Select
                        classNamePrefix="react-select"
                        placeholder="Search by Customer..."
                        options={customerFilterData}
                        value={selectedCustomer}
                        isClearable
                        onChange={handleCustomerChange}
                    />

                </div>
                <div className="col-md-4">
                    <Select
                        classNamePrefix="react-select"
                        placeholder="Search by State..."
                        options={stateFilterData}
                        value={selectedState}
                        isClearable
                        onChange={handleStateChange}
                    />
                    
                </div>
                <div className="col-md-4">
                    <button className="btn-link-style"
                        onClick={handleShareClick}
                        disabled={!isExportReady} 
                        style={{ 
                            opacity: isExportReady ? 1 : 0.5, 
                            cursor: isExportReady ? 'pointer' : 'not-allowed' 
                        }}
                    >
                        <img src={shareIcon} alt="share"/>Share
                    </button>
                    <button 
                        className="btn-link-style" 
                        onClick={handleDownloadExcel} 
                        disabled={!isExportReady} // Disable until charts are ready
                        style={{ opacity: isExportReady ? 1 : 0.5, cursor: isExportReady ? 'pointer' : 'not-allowed' }}
                    >
                        <img src={xlsIcon} alt="Excel" />Export Excel
                    </button>
                    <button 
                        className="btn-link-style" 
                        onClick={handleDownloadPDF}
                        disabled={!isExportReady} 
                        style={{ 
                            opacity: isExportReady ? 1 : 0.5, 
                            cursor: isExportReady ? 'pointer' : 'not-allowed' 
                        }}
                    >
                        <img src={pdfIcon} alt="Pdf" />Export PDF
                    </button>
                </div>
            </div>
            {renderTopCustomers()}
            <div className="row mt-4">
                {renderNewCustomers()}
                {renderDormantCustomers()}
            </div>
            {showLoader && (
                <Loader isLoading={showLoader} message="Processing..."/>
            )}
            {sharePayload && (
                <ShareSalesReportModal 
                    payload={sharePayload} 
                    isShow={showShareModal} 
                    onClose={() => setShowShareModal(false)} 
                />
            )}
        </div>
    );
};

export default CustomerRankingTab;